// Kit 02 · items 43, 44, 45, 46, 48, 49, 51, 52, 53.
//
//   43 Breadcrumb        icon 16 · gap 8 · ONE level only, never a trail
//   44 Module header     eyebrow row fixed 18 · title 32/-0.03em · 60ch · no stat strip
//   45 Section header    rule above · 20/600/-0.02em · gap 8 to content
//   46 Module tabs       underline 2 · gap 24 · padding 12 below · overlap 1
//   48 Stepper           node 28 · connector 2 · label gap 8 · inset 22
//   49 Pagination        cell 30 · gap 8 · radius 8 · tabular throughout
//   51 Mobile shell      header 52 · sheet 264 · scrim over canvas
//   52 Skip to content   height 36 · pad 0/12 · radius 8 · ring 2 offset 2
//   53 Footer            REMOVED from the kit. Nothing renders below the
//                        content column, so there is no component here.

import type { ReactNode } from "react";
import { Check } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./icon";
import { cx, stateAttr } from "./kit-state";
import type { KitState, Stateful } from "./kit-state";

/* ── 43 · breadcrumb ────────────────────────────────────────────────────────
   One level. Never a trail: the rail already says where you are, and a trail
   beside it is a second answer to a question nobody asked twice. */

export function Breadcrumb({ icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="k-crumb">
      <Icon icon={icon} />
      {children}
    </div>
  );
}

/* ── 44 · module header ─────────────────────────────────────────────────────
   The eyebrow row is a FIXED 18 whether or not it has content, so the title's
   baseline sits at the same height on every module and the page does not jump
   between them. No stat strip: the redline says so, and a strip here is how a
   header comes to restate a figure computed further down. */

export interface ModuleHeaderProps {
  eyebrow?: ReactNode;
  title: string;
  description?: string;
  className?: string;
}

export function ModuleHeader({ eyebrow, title, description, className }: ModuleHeaderProps) {
  return (
    <header className={cx("k-module-header", className)}>
      {/* Rendered even when empty. That is the point of a fixed height. */}
      <div className="k-module-header__eyebrow">{eyebrow}</div>
      <h1 className="k-module-header__title">{title}</h1>
      {description ? <p className="k-module-header__desc">{description}</p> : null}
    </header>
  );
}

/* ── 45 · section header ────────────────────────────────────────────────── */

export function SectionHeader({ title, children, className }: { title: string; children?: ReactNode; className?: string }) {
  return (
    <div className={cx("k-section-header", className)}>
      <h2 className="k-section-header__title">{title}</h2>
      {children}
    </div>
  );
}

/* ── 46 · module tabs, underline ────────────────────────────────────────────
   The overlap is the detail worth keeping: the selected tab's 2px underline
   sits ON the container's 1px rule rather than below it, so selecting a tab
   does not make the row one pixel taller. */

export interface TabItem {
  value: string;
  label: string;
  disabled?: boolean;
  state?: KitState;
  /**
   * A count chip inside the tab, for a tab that holds a COLLECTION.
   *
   * Additive, the same shape as the `labelHidden` Phase 6 added to Checkbox,
   * and it carries one rule with it: a tab that is a PHASE rather than a
   * collection must not have one. A chip reading "4" on a phase tab is read as
   * "four things in here" when it would mean "four things selected", and the
   * two are different claims. The component cannot enforce that — only the
   * caller knows which kind of tab it is — so the rule is stated here and the
   * caller is expected to leave it undefined.
   */
  badge?: ReactNode;
}

export interface TabsProps extends Stateful {
  items: TabItem[];
  value: string;
  onChange?: (value: string) => void;
  label: string;
  className?: string;
}

export function Tabs({ items, value, onChange, label, className }: TabsProps) {
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const usable = items.filter((t) => !t.disabled);
    if (usable.length === 0) return;
    const at = usable.findIndex((t) => t.value === value);
    let next = at;
    if (event.key === "ArrowRight") next = (at + 1) % usable.length;
    else if (event.key === "ArrowLeft") next = (at - 1 + usable.length) % usable.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = usable.length - 1;
    else return;
    event.preventDefault();
    const target = usable[next];
    onChange?.(target.value);
    (event.currentTarget.querySelector(`[data-value="${target.value}"]`) as HTMLElement | null)?.focus();
  }

  return (
    <div className={cx("k-tabs", className)} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {items.map((tab) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            data-value={tab.value}
            className="k-tabs__tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => onChange?.(tab.value)}
            {...stateAttr(tab.state ?? (selected ? "selected" : undefined))}
          >
            {tab.label}
            {tab.badge}
          </button>
        );
      })}
    </div>
  );
}

/* ── 48 · stepper ───────────────────────────────────────────────────────────
   Three states and no fourth. A step is complete, current, or upcoming; there
   is no "error" step, because a step that failed is the current step with
   something to say. */

export interface Step {
  label: string;
  state: "complete" | "current" | "upcoming";
}

export function Stepper({ steps, label, className }: { steps: Step[]; label: string; className?: string }) {
  const currentAt = steps.findIndex((s) => s.state === "current");
  return (
    <ol className={cx("k-stepper-nav", className)} aria-label={label}>
      {steps.map((step, index) => (
        <li key={step.label} className="k-step-item" style={{ display: "contents" }}>
          <div className="k-step" data-state={step.state} aria-current={step.state === "current" ? "step" : undefined}>
            <span className="k-step__node" data-num="true">
              {/* The number is replaced by a tick once done, so the node says
                  what happened rather than where it sits. */}
              {step.state === "complete" ? <Icon icon={Check} size="sm" /> : index + 1}
            </span>
            <span className="k-step__label">{step.label}</span>
            {/* The state each step is in is in DOM text, not only in colour. */}
            <span className="k-sr">{step.state}</span>
          </div>
          {index < steps.length - 1 ? (
            <span
              className="k-step__connector"
              data-state={index < currentAt ? "complete" : undefined}
              aria-hidden="true"
            />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/* ── 49 · pagination ────────────────────────────────────────────────────────
   Tabular figures throughout, so 1 and 9 occupy the same cell and the row does
   not shuffle as you page. The gap is not a control and does not look like one. */

export interface PaginationProps {
  page: number;
  pageCount: number;
  onChange?: (page: number) => void;
  className?: string;
  label?: string;
}

/** The window of page numbers to show, with nulls where a gap belongs.
    Derived from page and pageCount so the ellipsis can never appear beside a
    number it is hiding nothing behind. */
export function pageWindow(page: number, pageCount: number): Array<number | null> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out: Array<number | null> = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pageCount - 1, page + 1);
  if (from > 2) out.push(null);
  for (let n = from; n <= to; n++) out.push(n);
  if (to < pageCount - 1) out.push(null);
  out.push(pageCount);
  return out;
}

export function Pagination({ page, pageCount, onChange, className, label = "Pagination" }: PaginationProps) {
  const cells = pageWindow(page, pageCount);
  return (
    <nav className={cx("k-pagination", className)} aria-label={label}>
      {cells.map((n, i) =>
        n === null ? (
          <span key={`gap-${i}`} className="k-page-cell k-page-cell--gap" aria-hidden="true">…</span>
        ) : (
          <button
            key={n}
            type="button"
            className="k-page-cell"
            aria-current={n === page ? "page" : undefined}
            aria-label={`Page ${n}`}
            data-num="true"
            onClick={() => onChange?.(n)}
          >
            {n}
          </button>
        )
      )}
    </nav>
  );
}

/* ── 52 · skip to content ───────────────────────────────────────────────────
   The first tab stop in the document, off-screen until focused. It is the one
   control whose entire job is to be invisible until a keyboard user needs it. */

export function SkipLink({ targetId = "main", children = "Skip to content", state }: { targetId?: string; children?: ReactNode } & Stateful) {
  return (
    <a className="k-skip" href={`#${targetId}`} {...stateAttr(state)}>
      {children}
    </a>
  );
}

/* ── 51 · mobile shell ──────────────────────────────────────────────────────
   redline: header 52 · sheet 264 · scrim over canvas
   One scrim. The sheet sits at --z-drawer and its scrim at --z-scrim-page,
   which is BELOW it: a drawer's own scrim dims the page for it, it does not
   dim the drawer. */

export interface MobileShellProps {
  header: ReactNode;
  children: ReactNode;
  sheet?: ReactNode;
  sheetOpen?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export function MobileShell({ header, children, sheet, sheetOpen, onDismiss, className }: MobileShellProps) {
  return (
    <div className={cx("k-mobile", className)}>
      <div className="k-mobile__header">{header}</div>
      {children}
      {sheetOpen ? (
        <>
          <div className="k-mobile__scrim" onClick={onDismiss} />
          <div className="k-mobile__sheet" role="dialog" aria-modal="true" aria-label="Navigation">
            {sheet}
          </div>
        </>
      ) : null}
    </div>
  );
}
